import { useEffect } from "react";
import { guideReference, type LevelReference } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { useRoute } from "@/router";
import { useViewState } from "@/shell/viewState";
import { RatingLadder } from "@/ui/RatingScale";
import { QUESTIONS_LABEL } from "@/ui/vocabulary";
import { Banner, SectionBar, Skeleton, Table } from "@/ui";
import { focusSection } from "./Guide";

// V1-INTERACTIONS §4.25 — Interview Guidance: the ONE place the standing assessment guidance lives
// (CALL-17 32:19 / R-INT-140 / AF-4: "I don't think it should be part of the interview packet where you specify what
// ROOTS is every single time"). The five values and what each one assesses, the approved question set for each of
// them, the 1–5 ladder with the level's written anchors, and the recommendation routes with the decision rule.
// A packet links HERE instead of repeating it per candidate; the packet's own pinned pages are untouched (Law 3).
// Route: `#info` (the level of the first published guide) or `#info/<level>`.

export function Info() {
  const { register } = useViewState();
  const route = useRoute();
  const wanted = (route.parts[0] ?? "").split("?")[0] ?? "";
  const q = useRead(["guide", "reference"], () => guideReference({}));
  const levels = q.data?.levels ?? [];
  const level = levels.find(l => l.level === wanted) ?? levels[0];
  useEffect(
    () => register({ view: "info", ids: level ? { level: level.level } : {}, summary: `interview guidance · ${level?.level_label ?? "loading"}` }),
    [register, level],
  );
  return (
    <div className="doc">
      <h1 className="page">Interview Guidance</h1>
      <p className="lede">
        The standing guidance for every interview: what each GoToFoods ROOTS value assesses, the questions approved for
        the level, the 1–5 rating ladder and the recommendation routes. Your packet carries your own assignment; this is
        the reference behind it.
      </p>
      <Banner error={q.error} onRetry={() => void q.refetch()} />
      {q.isLoading && <Skeleton rows={6} />}
      {/* The server returns the three levels in the domain enum's order; this screen does not re-rank them. */}
      {!!levels.length && (
        <nav className="tabs tabs--page" aria-label="Role level">
          {levels.map(row => (
            <a key={row.level} href={`#info/${row.level}`} aria-current={level?.level === row.level ? "page" : undefined}>{row.level_label}</a>
          ))}
        </nav>
      )}
      {level && <LevelGuidance level={level} />}
    </div>
  );
}

function LevelGuidance({ level }: { level: LevelReference }) {
  return (
    <>
      <dl className="kv doc__meta">
        <div><dt>Level</dt><dd>{level.level_label}</dd></div>
        <div><dt>Core values assessed</dt><dd>All {level.values?.length ?? 5} — mandatory</dd></div>
        <div><dt>Minimum role-specific skills</dt><dd>{level.min_skills ?? "—"}</dd></div>
        <div><dt>Onsite rounds (max)</dt><dd>{level.onsite_cap ?? "—"}</dd></div>
        <div><dt>Total touchpoints (max)</dt><dd>{level.touchpoint_cap ?? "—"}</dd></div>
        <div><dt>Published guide</dt><dd>v{level.guide_version}</dd></div>
      </dl>
      <SectionBar>
        {(level.values ?? []).map(value => (
          <button key={value.key} type="button" aria-controls={`info-${value.key}`} onClick={() => focusSection(`info-${value.key}`)}>
            {value.name}
          </button>
        ))}
        <button type="button" aria-controls="info-ladder" onClick={() => focusSection("info-ladder")}>Rating ladder</button>
        <button type="button" aria-controls="info-routes" onClick={() => focusSection("info-routes")}>Recommendation</button>
      </SectionBar>
      {(level.values ?? []).map(value => (
        <section className="card doc__section" id={`info-${value.key}`} tabIndex={-1} key={value.key} aria-labelledby={`info-h-${value.key}`}>
          <h2 className="sec" id={`info-h-${value.key}`}>
            <span className="doc__n" aria-hidden="true">{value.name.slice(0, 1)}</span> {value.name}
          </h2>
          <p><b>Standard assessed.</b> {value.assesses}</p>
          <h3 className="sub">{level.questions_note ?? QUESTIONS_LABEL}</h3>
          <ol className="tiles tiles--plain">
            {(value.questions ?? []).map((question, index) => (
              <li className="tile" key={question}>
                <span className="tile__n" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                <div><p>{question}</p></div>
              </li>
            ))}
          </ol>
          <h3 className="sub">Anchors</h3>
          <RatingLadder anchors={value.anchors ?? {}} />
        </section>
      ))}
      <section className="card doc__section" id="info-ladder" tabIndex={-1} aria-labelledby="info-h-ladder">
        <h2 className="sec" id="info-h-ladder">The rating ladder</h2>
        {level.score_legend && <p>{level.score_legend}</p>}
        <Table label="Rating scale" mobile="stack" head={<tr><th>Score</th><th>What it means</th></tr>}>
          {["5", "4", "3", "2", "1"].map(score => (
            <tr key={score}>
              <td className="sticky-l num">{score}</td>
              <td>{level.score_descriptions?.[score] ?? "—"}</td>
            </tr>
          ))}
        </Table>
        {level.evidence_prompt && <p className="muted">Every rating needs written evidence: {level.evidence_prompt.toLowerCase()}.</p>}
      </section>
      <section className="card doc__section" id="info-routes" tabIndex={-1} aria-labelledby="info-h-routes">
        <h2 className="sec" id="info-h-routes">Hiring recommendation</h2>
        <ol className="tiles">
          {(level.routes ?? []).map((route, index) => (
            <li className="tile" key={route.key}>
              <span className="tile__n" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
              <div>
                <b className="tile__title">{route.label}</b>
                {route.description && <p>{route.description}</p>}
              </div>
            </li>
          ))}
        </ol>
        {!!level.bands?.length && (
          <>
            <h3 className="sub">Score bands</h3>
            <ul>{level.bands.map(band => <li key={band}>{band}</li>)}</ul>
            {level.bands_note && <p className="muted">{level.bands_note}</p>}
          </>
        )}
        {level.decision_rule_text && <div className="callout doc__rule"><h3 className="sub">Decision rule</h3><p>{level.decision_rule_text}</p></div>}
        {level.alignment_text && <div className="callout doc__rule"><h3 className="sub">Alignment requirement</h3><p>{level.alignment_text}</p></div>}
        {level.coc_question_text && <div className="callout doc__rule"><h3 className="sub">Code of Conduct</h3><p>{level.coc_question_text}</p></div>}
      </section>
    </>
  );
}
